import React, { useEffect, useState, useMemo } from "react";
import { Box, Text, HStack, Button, FormLabel } from "@chakra-ui/react";
import Select from "react-select";
import { FaPlus } from "react-icons/fa";
import { formatCentroCustoCodigoDisplay } from "../../../utils/centroCustoCodigoDisplay";

import { request } from "../../../services/fetch";

const normalizeForSearch = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const normalizeCodigo = (codigo) =>
  String(codigo || "")
    .trim()
    .replace(/,/g, ".")
    .split(".")
    .filter(Boolean)
    .map((parte) => {
      const numero = Number(parte);
      return Number.isNaN(numero) ? parte : String(numero);
    })
    .join(".");

/**
 * Seleção pesquisável de centro de custo (catálogo compartilhado entre clínicas).
 *
 * @param {string} props.clinicaId
 * @param {string} props.value ObjectId string ou vazio
 * @param {(id: string) => void} props.onChange
 * @param {boolean} [props.isRequired]
 * @param {boolean} [props.isDisabled]
 * @param {"receita"|"despesa"} [props.type] filtro por tipo de centro de custo
 * @param {() => void} [props.onOpenCadastro] botão "+" ao lado do rótulo
 * @param {number} [props.refreshKey] incrementar após cadastrar novo centro
 * @param {string} [props.label] rótulo exibido acima do select
 * @param {boolean} [props.visualizacaoGeral] exibe todos os centros de custo
 * @param {string} [props.codigoPrincipal] limita a lista ao código e seus descendentes
 * @param {string} [props.centroCustoRaizId] usa o centro informado como raiz do filtro
 * @param {boolean} [props.incluirRaiz] inclui a própria raiz entre as opções filtradas
 * @param {boolean} [props.exigirRaiz] sem uma raiz, não exibe opções no modo restrito
 */
export default function CentroCustoSelect({
  clinicaId,
  value,
  onChange,
  isRequired = false,
  isDisabled = false,
  type,
  onOpenCadastro,
  refreshKey = 0,
  label = "Centro de custo",
  visualizacaoGeral = true,
  codigoPrincipal,
  centroCustoRaizId,
  incluirRaiz = true,
  exigirRaiz = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clinicaId) {
      setOptions([]);
      return;
    }
    const query = new URLSearchParams({ active: "false" });
    if (type) query.set("type", type);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await request(`api/financas/centrocusto/clinica/${clinicaId}?${query.toString()}`);
        if (!cancelled && Array.isArray(data)) setOptions(data);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicaId, refreshKey, type]);

  const selected = useMemo(() => {
    if (!value || !options.length) return null;
    return options.find((o) => (String(o._id) === String(value) || o.aliasIds?.includes(String(value)))) || null;
  }, [value, options]);

  const visibleOptions = useMemo(() => {
    if (visualizacaoGeral) return options;

    const configuredRoot = centroCustoRaizId
      ? options.find((option) => (String(option._id) === String(centroCustoRaizId) || option.aliasIds?.includes(String(centroCustoRaizId))))
      : null;
    // Mantém o comportamento anterior para os campos existentes: quando nenhuma
    // raiz explícita é informada, o valor atual pode servir como raiz.
    const rootCode = configuredRoot?.codigo || codigoPrincipal || selected?.codigo;
    if (!rootCode) return exigirRaiz ? [] : options;

    const normalizedRoot = normalizeCodigo(rootCode);
    return options.filter((option) => {
      const optionCode = normalizeCodigo(option.codigo);
      return (incluirRaiz && optionCode === normalizedRoot) || optionCode.startsWith(`${normalizedRoot}.`);
    });
  }, [centroCustoRaizId, codigoPrincipal, exigirRaiz, incluirRaiz, options, selected, visualizacaoGeral]);

  const filterOption = (option, rawInput) => {
    const q = normalizeForSearch(rawInput);
    if (!q) return true;
    const n = option.data;
    const codigoRaw = normalizeForSearch(n.codigo || "");
    const codigoFmt = normalizeForSearch(
      formatCentroCustoCodigoDisplay(n.codigo || ""),
    );
    return (
      codigoRaw.includes(q) ||
      codigoFmt.includes(q) ||
      normalizeForSearch(n.nome || "").includes(q) ||
      normalizeForSearch(n.caminhoBusca || "").includes(q)
    );
  };

  const formatOptionLabel = (n) => (
    <Box lineHeight="short" py={0.5}>
      <Text fontWeight="medium" noOfLines={1}>
        {formatCentroCustoCodigoDisplay(n.codigo)} — {n.nome}
      </Text>
      <Text fontSize="xs" color="gray.600" noOfLines={2}>
        {n.caminhoBusca || "—"}
      </Text>
    </Box>
  );

  return (
    <Box>
      <HStack mb={2} align="center">
        <FormLabel mb={0}>{label}</FormLabel>
        {onOpenCadastro && (
          <Button
            size="xs"
            colorScheme="teal"
            onClick={onOpenCadastro}
            type="button"
          >
            <FaPlus size="12px" />
          </Button>
        )}
      </HStack>
      <Select
        classNamePrefix="centro-custo"
        placeholder="Digite para filtrar..."
        isClearable
        isDisabled={isDisabled || !clinicaId}
        isLoading={loading}
        options={visibleOptions}
        value={selected}
        onChange={(opt) => onChange(opt ? String(opt._id) : "")}
        getOptionValue={(n) => String(n._id)}
        getOptionLabel={(n) =>
          `${formatCentroCustoCodigoDisplay(n.codigo)} ${n.nome}`
        }
        formatOptionLabel={formatOptionLabel}
        filterOption={filterOption}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        menuPosition="fixed"
        styles={{
          container: (base) => ({ ...base, width: "100%" }),
          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
        }}
      />
    </Box>
  );
}
